import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, Pencil } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Breadcrumbs } from "../components/layout/Breadcrumbs";
import { PageTitle } from "../components/layout/PageTitle";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { ErrorState } from "../components/ErrorState";
import { EmptyState } from "../components/EmptyState";
import { EventStatusBadge } from "../components/EventStatusBadge";
import { EventFormModal } from "../components/event/EventFormModal";
import { useAuth } from "../hooks/useAuth";
import { listEvents } from "../services/event.service";
import { getApiErrorMessage } from "../lib/apiClient";
import type { EventRecord } from "../types";

export function canEditEvent(event: EventRecord, userId: string | undefined, role: string | undefined): boolean {
  if (role === "SUPER_ADMIN") return true;
  if (!userId) return false;
  return !!event.assignments?.some((a) => a.userId === userId && a.roleInEvent === "MANAGER");
}

export default function EventsPage(): JSX.Element {
  const { user } = useAuth();
  const { data, isLoading, isError, error } = useQuery({ queryKey: ["events"], queryFn: listEvents });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<EventRecord | null>(null);

  return (
    <DashboardLayout>
      <Breadcrumbs items={[{ label: "Events" }]} />
      <div className="flex items-center justify-between">
        <PageTitle icon={CalendarDays} title="Events" />
        {user?.role === "SUPER_ADMIN" && (
          <button type="button" className="btn-primary" onClick={() => setModalOpen(true)}>
            New Event
          </button>
        )}
      </div>

      <div className="mt-6">
        {isLoading && <LoadingSpinner />}
        {isError && <ErrorState message={getApiErrorMessage(error)} />}
        {data && data.length === 0 && (
          <EmptyState
            title="No events"
            description={
              user?.role === "SUPER_ADMIN"
                ? "Create an event to get started."
                : "You have not been assigned to any events yet."
            }
          />
        )}
        {data && data.length > 0 && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {data.map((event) => (
              <Link
                key={event.id}
                to={`/events/${event.id}`}
                className="card relative p-4 hover:border-brand-300 dark:hover:border-brand-500 transition-colors duration-150"
              >
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100">{event.name}</h3>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <EventStatusBadge status={event.status} />
                    {canEditEvent(event, user?.id, user?.role) && (
                      <button
                        type="button"
                        className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-600 dark:hover:text-slate-200"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setEditingEvent(event);
                        }}
                        aria-label={`Edit ${event.name}`}
                        title="Edit event"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{event.course?.name}</p>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">
                  {new Date(event.eventDate).toLocaleDateString()} - {event.startTime} to {event.endTime}
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300">{event.location}</p>
              </Link>
            ))}
          </div>
        )}
      </div>

      {user?.role === "SUPER_ADMIN" && <EventFormModal open={modalOpen} onClose={() => setModalOpen(false)} />}
      <EventFormModal open={!!editingEvent} onClose={() => setEditingEvent(null)} initial={editingEvent} />
    </DashboardLayout>
  );
}
